import { defineStore } from 'pinia'
import { usePersistentStore } from '~/stores/persistent'
import { getAuthKeyFromSetCookies } from '~/utils/auth'
import { addCommonQueryParam } from '~/utils/general'
import { shortenCategoryIds } from '~/utils/categoryIds'
import type { CategoryItem, Breadcrumb } from '~/types/root'
import type { ActionsNeeded, AccountNotices } from '~/types/account'
import type { ProductAPIResponse } from '~/types/shop'
import { assertIsDefined } from "~/utils/assert"

interface AnonymousSessionResponse {
  cookieExpiration: number
}

export const useRootStore = defineStore('root', () => {

  const timeoutMenu = ref(false)
  const recaptchaToken = ref<string>()
  const recaptchaAction = ref<string>()
  const recaptchaError = ref<string>()
  const authMessage = ref<string>()
  const categories = ref<CategoryItem[]>([])
  const redirectRoute = ref<string>()
  const checkoutClicked = ref(false)
  const cartFailureDialog = ref(false)
  const rootLoading = ref(false)
  // Set when this request minted an anonymous session (fetchAnonymousSession).
  // Read by the pre-attribution telemetry to tell "attributed in this request"
  // apart from an attribution carried over from a previous session.
  const sessionFreshlyMinted = ref(false)
  const signifydSessionId = ref<string>()
  const clientIpAddress = ref<string>()
  const persistentStore = usePersistentStore()

  const auth = useSAuth()
  const api = $api()

  const isActiveDemo = computed(() => {
    try {
      return !!persistentStore.getPersistentValue('isDemo')
    } catch {
      return false
    }
  })

  const accountNotices = computed(() => {
    if (auth.user.value) {
      const actionsNeeded = auth.user.value.actionsNeeded as ActionsNeeded
      return {
        vatRegistration: actionsNeeded?.needsGstNumberConfirmation || actionsNeeded?.needsGstIDConfirmation || actionsNeeded?.needsVatNumberFormatCorrection ? 'error' : undefined,
        billing: actionsNeeded?.needsDirectDepositAccount ? 'error' : undefined,
        agreements: actionsNeeded?.needsDemoLocatorSla || actionsNeeded?.needsDemoDirectorySla ? '#F6A94F' : actionsNeeded?.needsIdaAcceptance ? 'error' : undefined,
        settings: actionsNeeded?.needsTeamLeaderSelection ? 'error' : undefined
      } as AccountNotices
    }
    return {
      vatRegistration: undefined,
      billing: undefined,
      agreements: undefined,
      settings: undefined
    }
  })

  const updateTimeoutMenu = (show: boolean) => {
    timeoutMenu.value = show
    return timeoutMenu.value
  }

  const fetchAnonymousSession = async () => {
    const accountResponse = await api.raw<AnonymousSessionResponse>('account/anonymous')
    const accountData = accountResponse._data
    assertIsDefined(accountData, 'anonymous session response should include body data')
    const cookieExpiration = new Date(accountData.cookieExpiration * 1000)
    persistentStore.updateValue('expirationDialogTime', accountData.cookieExpiration, cookieExpiration)
    persistentStore.updateValue('isDemo', false)
    if (import.meta.server) {
      const token = getAuthKeyFromSetCookies(accountResponse.headers.getSetCookie())
      assertIsDefined(token, 'token cookie header should be set when login via token')
      auth.setAuthCookie(token)
      auth.token.value = token
      auth.loggedIn.value = true
    } else {
      // Client-side mints (logout, locale domain-change, expiry re-mint) return
      // fresh cookies; clear the impersonation context so a stale agent view
      // doesn't survive the new session. Ports nuxt2 stores/root.ts:112.
      persistentStore.clearImpersonationContext()
      await auth.fetchSession()
    }

    sessionFreshlyMinted.value = true
  }

  const fetchCategories = async () => {
    const query = addCommonQueryParam()
    const categoriesResponse = await api<CategoryItem[]>('categories/new', { query })
    if (categoriesResponse) {
      categories.value = shortenCategoryIds(categoriesResponse)
    }
  }

  const findCategoryParents = (categories: CategoryItem[], slug: string): Breadcrumb[] => {
    let result: Breadcrumb[] = []
    categories.forEach((category) => {
      if (category.slug === slug) {
        result = [{
          name: category.name,
          slug: category.slug
        }]
        return
      }
      const temp = findCategoryParents(category.children || [], slug)
      if (temp.length > 0 && category.slug) {
        result = [{
          name: category.name,
          slug: category.slug
        }, ...temp]
      }
    })
    return result
  }

  const getCategoryBreadcrumbs = (slug: string, includeGroup = false) => {
    for (const categoryResponse of categories.value) {
      if (categoryResponse.children) {
        for (const group of categoryResponse.children) {
          if (includeGroup && group.slug === slug) {
            return [{ name: group.name, slug: group.slug }]
          }
          const breadcrumbs = findCategoryParents(group.children || [], slug)
          if (breadcrumbs.length > 0) {
            if (includeGroup && group.name && group.slug) {
              return [{ name: group.name, slug: group.slug }, ...breadcrumbs]
            }
            return breadcrumbs
          }
        }
      }
    }
    return []
  }

  const findCategoryChain = (slug: string): Breadcrumb[] => {
    const recurse = (items: CategoryItem[]): Breadcrumb[] => {
      for (const item of items) {
        if (!item.slug) { continue }
        if (item.slug === slug) {
          return [{ name: item.name, slug: item.slug, hasContent: item.hasContent }]
        }
        const chain = recurse(item.children || [])
        if (chain.length > 0) {
          return [{ name: item.name, slug: item.slug, hasContent: item.hasContent }, ...chain]
        }
      }
      return []
    }
    return recurse(categories.value)
  }

  // Must go through getCanonicalCategory, not raw longest-wins: this feeds the
  // JSON-LD `category` field, while the visible breadcrumb and the GTM
  // `item_category` already resolve through the canonical helper. Longest-wins
  // here made the three disagree for a SparkIt product cross-merchandised into a
  // department with a longer slug. Matches Nuxt2 stores/root.ts:171-175.
  const getSeoCategory = (product: ProductAPIResponse) => {
    const canonical = getCanonicalCategory(product.categorySlugs)
    if (!canonical) { return '' }
    return getCategoryBreadcrumbs(canonical)
      .map(el => el.name)
      .join(' / ')
  }

  const demoLoginRedirect = ref(true)
  return {
    accountNotices,
    authMessage,
    cartFailureDialog,
    categories,
    checkoutClicked,
    clientIpAddress,
    demoLoginRedirect,
    isActiveDemo,
    recaptchaToken,
    recaptchaAction,
    recaptchaError,
    redirectRoute,
    rootLoading,
    sessionFreshlyMinted,
    signifydSessionId,
    timeoutMenu,
    fetchAnonymousSession,
    fetchCategories,
    findCategoryChain,
    getCategoryBreadcrumbs,
    getSeoCategory,
    updateTimeoutMenu
  }
})
